import { addHours, addMinutes, endOfDay, format } from "date-fns";
import { uniq } from "lodash-es";

import { getDateWithTime } from "@/lib/utils";
import {
  GuestType,
  PublicSession,
  RecurringEndType,
  RecurringFrequency,
  UpdateRecurringSessionDto,
  UpdateSessionDto,
} from "@classview/shared-types";

import {
  AddSessionFormData,
  SessionVisibility,
} from "../types/add-session-schema";

export function transformApiDataToFormData(
  data?: PublicSession,
  defaultDate?: string,
): AddSessionFormData {
  if (!data) {
    const date = defaultDate ? new Date(defaultDate) : new Date();

    return {
      name: "",
      description: "",
      // Round current time up to the next 5-minute interval for user convenience
      startTime: addMinutes(new Date(), 5 - (new Date().getMinutes() % 5)),
      endTime: addMinutes(
        addHours(new Date(), 1),
        5 - (new Date().getMinutes() % 5),
      ),
      startDate: date,
      isScheduledToRecord: false,
      visibility: SessionVisibility.PRIVATE,
      isConfidential: false,
      isParticipantScreenSharing: false,
      isClosedCaptionsEnabled: false,
      guests: [],
      roomSystems: [],
      isRecurring: false,
      recurringFrequency: RecurringFrequency.DAILY,
      isRepeatOnWeekdaysOnly: false,
      weeklyDays: [],
      weeklyInterval: 1,
      endDate: undefined,
      endType: RecurringEndType.NB_REPETITIONS,
      nbRepetitions: 1,
      invitedOrganisations: [],
      isRaisedHandSoundEnabled: true,
      isChatEnabled: true,
    };
  }
  return {
    name: data.name,
    description: data.description || "",
    startDate: new Date(data.start),
    startTime: new Date(data.start),
    endTime: new Date(data.end),
    isScheduledToRecord: data.isScheduledToRecord,
    visibility: data.isPrivate
      ? SessionVisibility.PRIVATE
      : SessionVisibility.ORGANISATION_WIDE,
    isConfidential: data.isConfidential,
    isParticipantScreenSharing: data.room.isParticipantScreenSharing,
    isClosedCaptionsEnabled: data.isCCEnabled,
    guests:
      data.attendees?.map((attendee) => ({
        userId: attendee.type === GuestType.User ? attendee.id : undefined,
        email: attendee.email.toLowerCase().trim(),
        firstName: attendee.firstName ?? "",
        lastName: attendee.lastName ?? "",
        isExternal: attendee.isExternal,
        isModerator: attendee.isModerator,
        isHost: attendee.isHost,
      })) ?? [],
    roomSystems:
      data.roomSystems?.map((roomSystem) => ({
        id: roomSystem.id,
        name: roomSystem.name,
        organisationId: roomSystem.organisation.id,
        organisationName: roomSystem.organisation.name,
        conflictingSessions: roomSystem?.sessions?.conflictingSessions || [],
      })) ?? [],
    isRecurring: data.recurringDetails !== null,
    recurringFrequency:
      data.recurringDetails?.recurringFrequency ?? RecurringFrequency.DAILY,
    isRepeatOnWeekdaysOnly:
      data.recurringDetails?.recurringFrequency ===
      RecurringFrequency.DAILY_NOT_WEEKENDS,
    weeklyDays: data.recurringDetails?.weeklyDays ?? [],
    weeklyInterval: data.recurringDetails?.weeklyInterval ?? 1,
    endDate: data.recurringDetails?.endDate
      ? new Date(data.recurringDetails.endDate)
      : undefined,
    endType:
      data.recurringDetails?.recurringEndType ??
      RecurringEndType.NB_REPETITIONS,
    nbRepetitions: data.recurringDetails?.nbRepetitions ?? 1,
    invitedOrganisations: data.invitedOrganisations.map((organisation) => ({
      id: organisation.id,
      name: organisation.name,
      status: organisation.status,
    })),
    isRaisedHandSoundEnabled: data.isRaisedHandSoundEnabled,
    isChatEnabled: data.isChatEnabled,
  };
}

export function transformFormDataToApiData(
  data: AddSessionFormData,
): UpdateSessionDto {
  const from = getDateWithTime(data.startDate, data.startTime);
  const to = getDateWithTime(data.startDate, data.endTime);

  return {
    name: data.name,
    description: data.description,
    start: from.toISOString(),
    end: to.toISOString(),
    guests: data.guests?.map((guest) => guest.email.toLowerCase().trim()) ?? [],
    moderatorIds:
      data.guests
        ?.filter((guest) => guest.isModerator)
        .map((guest) => guest.userId)
        .filter((id) => id !== undefined) ?? [],
    isPrivate: data.visibility === SessionVisibility.PRIVATE,
    isConfidential: data.isConfidential,
    isScheduledToRecord: data.isScheduledToRecord,
    isParticipantScreenSharing: data.isParticipantScreenSharing,
    isClosedCaptionsEnabled: data.isClosedCaptionsEnabled,
    roomSystemIds: data.roomSystems?.map((roomSystem) => roomSystem.id) ?? [],
    organisationIds:
      data.invitedOrganisations
        ?.map((organisation) => organisation.id)
        .filter((id) => id !== undefined) ?? [],
    isRaisedHandSoundEnabled: data.isRaisedHandSoundEnabled,
    isChatEnabled: data.isChatEnabled,
  };
}

export function transformRecurringFormDataToApiData({
  data,
  recurringId,
  shouldUpdateAllSessions = false,
}: {
  data: AddSessionFormData;
  recurringId: string;
  shouldUpdateAllSessions?: boolean;
}): UpdateRecurringSessionDto {
  return {
    ...transformFormDataToApiData(data),
    isApplyingToAll: shouldUpdateAllSessions,
    recurringId,
    isDisablingEmails: false,
    endDateRecurring:
      data.endType === RecurringEndType.DATE && data.endDate
        ? format(endOfDay(data.endDate), "yyyy-MM-dd")
        : undefined,
    nbRepetitions:
      data.endType === RecurringEndType.NB_REPETITIONS
        ? data.nbRepetitions
        : undefined,
    recurringFrequency: data.recurringFrequency,
    weeklyDays: data.weeklyDays,
    weeklyInterval: data.weeklyInterval,
  };
}

export function transformAcceptSessionApiDataToFormData(
  data?: PublicSession,
): AddSessionFormData {
  return {
    ...transformApiDataToFormData(data),
    guests: [],
    roomSystems: [],
  };
}

/**
 * You need to pass all the guests and room systems to the api,
 * not just the ones that are being updated.
 */
export function transformAcceptSessionApiDataToApiData({
  data,
  session,
}: {
  data: AddSessionFormData;
  session: PublicSession;
}): UpdateSessionDto {
  const guests = uniq([
    ...(session.attendees?.map((attendee) =>
      attendee.email.toLowerCase().trim(),
    ) ?? []),
    ...(data.guests?.map((guest) => guest.email.toLowerCase().trim()) ?? []),
  ]);

  const roomSystems = uniq([
    ...(session.roomSystems?.map((roomSystem) => roomSystem.id) ?? []),
    ...(data.roomSystems?.map((roomSystem) => roomSystem.id) ?? []),
  ]);

  return {
    guests,
    roomSystemIds: roomSystems,
    isAcceptingInvite: true,
  };
}
